import React, { useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty, isNil } from 'ramda'
import { useIntl } from 'react-intl'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import {
  Avatar,
  Typography,
  makeStyles,
  Button,
  IconButton,
} from '@material-ui/core'

import PlainText from 'app/componentV4/PlainText'
import Linear from 'app/componentV4/Progress/Linear'
import Voters from 'app/componentV4/MessageDetail/Poll/Voters'
import usePoll from 'app/componentV4/MessageDetail/Poll/usePoll'
import Additional from 'app/componentV4/MessageDetail/Poll/Additional'
import LoaderPoll from 'app/componentV4/MessageDetail/Loader/LoaderPoll'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiAvatar-root': {
      width: '50px',
      height: '50px',
      '& .MuiSvgIcon-root': {
        display: 'none',
      },
    },
    '& .MuiAvatar-colorDefault': {
      color: theme.colors['neutrals-100'],
      backgroundColor: theme.colors['neutrals-100'],
    },
  },
}))

const PollListOnDetail = ({
  notiId,
  pollId,
  isAnonymous = false,
  languageId,
  isDetail = true,
}) => {
  const {
    state: { pollDetail, isLoadingPollDetail, voters, isLoadingVoters = null },
    handleFetchPollDetail,
    handleFetchVoters,
  } = usePoll()
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenDetail, setIsOpenDetail] = useState(false)

  const fetchPollOptions = useCallback(() => {
    handleFetchPollDetail(notiId, pollId, languageId)
  }, [handleFetchPollDetail, languageId, notiId, pollId])

  const handleRenderVotes = (optionId, languageId) => {
    setIsOpenDetail(true)
    handleFetchVoters(pollId, optionId, languageId)
  }

  useEffect(fetchPollOptions, [languageId])

  return (
    <div>
      {isLoadingPollDetail ? (
        <LoaderPoll />
      ) : (
        <>
          {!isEmpty(pollDetail) && (
            <div className={classes.root}>
              <Typography className="text-primary font-bold text-16">
                <PlainText text={pollDetail?.pollTitle} />
              </Typography>
              {!isNil(pollDetail.pollAdditionalContext) && isDetail && (
                <Button
                  className="text-14 text-sky-600 normal-case p-0 h-0 hover:bg-transparent"
                  disableRipple
                  onClick={() => {
                    setIsOpen(true)
                  }}
                >
                  {formatMessage({
                    id: 'timeline.detail.message.poll.additional.button',
                  })}
                </Button>
              )}
              <div className="space-y-10 my-10">
                {pollDetail?.options.map((option) => (
                  <div
                    id={`options-poll-${option.id}-${option.title}-detail`}
                    key={`${option.id}-${option.title}`}
                    className="flex space-x-5 items-center"
                  >
                    <div className="w-full">
                      <div className="flex justify-between items-end space-x-5">
                        <Typography className="text-16 font-400 text-black">
                          {option.title}
                        </Typography>
                        <Typography className="text-12 font-400 text-grey-300 whitespace-nowrap">
                          {`${Math.round(option.votedPercentage)}% (${option.votedCount})`}
                        </Typography>
                      </div>
                      <Linear
                        variant="determinate"
                        value={option.votedPercentage}
                      />
                    </div>

                    <Avatar
                      src={option.imageURL}
                      alt={option.title}
                      className="rounded-8"
                    />

                    {isDetail && (
                      <IconButton
                        aria-label="explan"
                        onClick={() => handleRenderVotes(option.id, languageId)}
                        className="w-20 h-20 m-auto"
                      >
                        <ArrowForwardIosIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <Additional
        text={pollDetail.pollAdditionalContext}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
      <Voters
        data={voters}
        isOpen={isOpenDetail}
        isAnonymous={isAnonymous}
        isLoading={isLoadingVoters}
        onClose={() => setIsOpenDetail(false)}
      />
    </div>
  )
}

PollListOnDetail.propTypes = {
  notiId: PropTypes.number,
  pollId: PropTypes.number,
  isDetail: PropTypes.bool,
  isAnonymous: PropTypes.bool,
  languageId: PropTypes.number,
}

export default PollListOnDetail
